import React from "react";
import _ from "lodash";

// Import Highcharts
import Highcharts from "highcharts";
import drilldown from "highcharts/modules/drilldown.js";
import HighchartsReact from "highcharts-react-official";

import InfoDescriptionTooltip from "./InfoDescriptionTooltip";
import { formatMoneyWithCurrency } from "./ReportsHelper";
// import PopoverForInfoDescription from "./PopoverForInfoDescription";

drilldown(Highcharts);

Highcharts.setOptions({
  lang: {
    decimalPoint: ".",
    thousandsSep: ",",
  },
  credits: {
    enabled: false,
  },
});

class RevenueDistributionDrilldownPieChart extends React.Component {
  constructor(props) {
    super(props);

    const {
      reports,
      metricKey,
      selectedCurrency,
      selectedPubId,
      selectedNetworkId,
      selectedYieldSetIds,
    } = props;

    this.state = {
      metricKey: props.metricKey,
      options: _createChartOptions({
        reports,
        metricKey,
        selectedCurrency,
        selectedPubId,
        selectedNetworkId,
        selectedYieldSetIds,
      }),
    };

    this.chartRef = React.createRef();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.startDate !== this.props.startDate ||
      prevProps.endDate !== this.props.endDate ||
      prevProps.selectedPubId !== this.props.selectedPubId ||
      prevProps.selectedNetworkId !== this.props.selectedNetworkId ||
      prevProps.selectedYieldSetIds !== this.props.selectedYieldSetIds ||
      prevProps.selectedCurrency !== this.props.selectedCurrency
    ) {
      // console.log("should update distribution charts");
      const {
        reports,
        metricKey,
        selectedCurrency,
        selectedPubId,
        selectedNetworkId,
        selectedYieldSetIds,
      } = this.props;

      const options = _createChartOptions({
        reports,
        metricKey,
        selectedCurrency,
        selectedPubId,
        selectedNetworkId,
        selectedYieldSetIds,
      });

      this.setState({
        options,
      });
    }
  }

  render() {
    const { options } = this.state;
    const { selectedPubId, selectedNetworkId, selectedYieldSetIds } =
      this.props;

    let chartTitleEle = "";
    if (selectedPubId === -1) {
      chartTitleEle = <>Publishers</>;
    } else if (selectedNetworkId !== -1 || selectedYieldSetIds !== -1) {
      chartTitleEle = (
        <>
          Yield Sets
          <InfoDescriptionTooltip
            metricKey="YIELD_SET"
            place="top"
          ></InfoDescriptionTooltip>
        </>
      );
    } else {
      chartTitleEle = <>Networks</>;
    }

    return (
      <div className="pt-4 pb-1">
        <div className="text-lg font-semibold text-gray-800">
          <div className="flex items-center justify-center">
            {chartTitleEle}
          </div>
        </div>

        <HighchartsReact
          highcharts={Highcharts}
          options={options}
          allowChartUpdate={true}
          ref={this.chartRef}
          immutable={true}
        />
      </div>
    );
  }
}

function _createChartOptionsByPublisher({
  reports,
  metricKey,
  selectedCurrency,
  selectedPubId,
}) {
  // Breakdown by:
  // 1. Publishers
  // 2. Networks
  // 3. YieldSets

  let pubTotal = _.sumBy(reports.publishers, metricKey);

  let selectedNetworkPerc, selectedNetworkName;

  const pubData = _.map(reports.publishers, (n) => {
    const d = n[metricKey];
    const p = _.round((d / pubTotal) * 100, 2);

    const valueFormatted = formatMoneyWithCurrency({
      currency: selectedCurrency,
      value: d,
    });

    if (n.id === selectedPubId) {
      selectedNetworkPerc = p;
      selectedNetworkName = n.name;
    }

    const r = {
      name: n.name,
      y: d,
      valueFormatted,
      perc: p,
    };

    return r;
  });

  const metricKeyName = _.startCase(metricKey);

  const options = {
    title: {
      text: "",
    },
    chart: {
      type: "pie",
      height: 242,
      backgroundColor: "rgba(0,0,0,0)",
    },

    plotOptions: {
      pie: {
        size: "100%",
        // showInLegend: true,
        dataLabels: {
          enabled: true,
          distance: 4,
          format: "<b>{point.name}</b> ({point.percentage:.2f}%)",
        },
        states: {
          hover: {
            halo: {
              opacity: 0.1,
            },
          },
        },
      },
    },

    tooltip: {
      useHTML: true,
      formatter: function () {
        return `
          <table>
            <thead>
              <tr>
                <th style="font-size: 14px; font-weight: bold;" colspan="2">
                  ${this.point.name}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  ${metricKeyName}
                </td>
                <td style="font-weight: bold;">
                  ${this.point.valueFormatted}
                </td>
              </tr>
              <tr>
                <td>
                  % of total
                </td>
                <td style="font-weight: bold;">
                  ${this.point.perc}%
                </td>
              </tr>
            </tbody>
          </table>
        `;
      },
    },

    series: [
      {
        name: "Publishers",
        // colorByPoint: true,
        data: pubData,
      },
    ],
  };

  return options;
}

function _createChartOptions({
  reports,
  metricKey,
  selectedCurrency,
  selectedPubId,
  selectedNetworkId,
  selectedYieldSetIds,
}) {
  // Breakdown by:
  // 1. Publishers
  // 2. Networks
  // 3. YieldSets
  // console.log(reports);
  if (selectedPubId === -1) {
    return _createChartOptionsByPublisher({
      reports,
      metricKey,
      selectedCurrency,
      selectedPubId,
      selectedNetworkId,
      selectedYieldSetIds,
    });
  }

  if (selectedPubId !== -1 && selectedNetworkId === -1) {
    reports.networks = _.filter(reports.networks, { pubId: selectedPubId });
  }

  let networkTotal = _.sumBy(reports.networks, metricKey);
  let ysTotal = _.sumBy(reports.yieldSets, metricKey);

  let isDrilldown = true;
  if (selectedNetworkId !== -1 || selectedYieldSetIds !== -1) {
    isDrilldown = false;
  }

  let selectedNetworkPerc, selectedNetworkName;

  const networkData = _.map(reports.networks, (n) => {
    const d = n[metricKey];
    const p = _.round((d / networkTotal) * 100, 2);

    const valueFormatted = formatMoneyWithCurrency({
      currency: selectedCurrency,
      value: d,
    });

    if (n.id === selectedNetworkId) {
      selectedNetworkPerc = p;
      selectedNetworkName = n.name;
    }

    const r = {
      name: n.name,
      y: d,
      valueFormatted,
      perc: p,
    };

    return r;
  });

  const ysDataGroupByNetwork = _.groupBy(reports.yieldSets, "gamNetworkId");
  let ysSeriesData = [];
  _.forEach(ysDataGroupByNetwork, (yieldSets, networkId) => {
    const ysData = {
      name: networkId,
      id: networkId,
      data: _.map(yieldSets, (ys) => {
        const d = ys[metricKey];
        const p = _.round((d / ysTotal) * 100, 2);

        const valueFormatted = formatMoneyWithCurrency({
          currency: selectedCurrency,
          value: d,
        });

        let isSelectedYs = _.indexOf(selectedYieldSetIds, ys.id) !== -1;

        const r = {
          name: ys.name,
          y: d,
          valueFormatted,
          perc: p,
        };

        if (selectedYieldSetIds !== -1) {
          r.color = isSelectedYs ? "" : "lightgray";
          r.dataLabels = {
            enabled: isSelectedYs,
            color: isSelectedYs ? "#000" : "lightgray",
          };

          r.sliced = isSelectedYs;
          r.selected = isSelectedYs;
        }

        return r;
      }),
    };

    ysSeriesData.push(ysData);
  });

  const metricKeyName = _.startCase(metricKey);

  const options = {
    title: {
      text: "",
    },
    chart: {
      type: "pie",
      height: 242,
      backgroundColor: "rgba(0,0,0,0)",
    },

    plotOptions: {
      pie: {
        size: "100%",
        // showInLegend: true,
        dataLabels: {
          enabled: true,
          distance: 4,
          format: "<b>{point.name}</b> ({point.percentage:.2f}%)",
        },
        states: {
          hover: {
            halo: {
              opacity: 0.1,
            },
          },
        },
      },
    },

    tooltip: {
      useHTML: true,
      formatter: function () {
        return `
          <table>
            <thead>
              <tr>
                <th style="font-size: 14px; font-weight: bold;" colspan="2">
                  ${this.point.name}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  ${metricKeyName}
                </td>
                <td style="font-weight: bold;">
                  ${this.point.valueFormatted}
                </td>
              </tr>
              <tr>
                <td>
                  % of total
                </td>
                <td style="font-weight: bold;">
                  ${this.point.perc}%
                </td>
              </tr>
            </tbody>
          </table>
        `;
      },
    },

    series: [
      {
        name: isDrilldown ? "Networks" : "Yield Sets",
        // colorByPoint: true,
        data: isDrilldown
          ? networkData
          : _.get(
              _.find(ysSeriesData, { id: selectedNetworkId.toString() }),
              "data",
              []
            ),
      },
    ],
  };

  return options;
}

export default RevenueDistributionDrilldownPieChart;
